// useSvgViewport — Pan/Zoom Hook für SVG
// Buttons = Zoom, Space+Drag oder Mittelklick-Drag = Pan
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';

const DEFAULT_ZOOM = 0.8;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;

// Keep smaller views inside the plan; center the plan when an axis has spare space.
const clampAxis = (position, visibleSize, contentSize) => visibleSize >= contentSize
  ? (contentSize - visibleSize) / 2
  : Math.max(0, Math.min(contentSize - visibleSize, position));

export function useSvgViewport({ contentWidth = 1200, contentHeight = 800, enableDragPan = true } = {}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [viewportSize, setViewportSize] = useState(null);

  // The SVG can mount after loading finishes. Observe the current node on each
  // render so its real dimensions, including sidebar and window resizing, win.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => {
      const { width, height } = node.getBoundingClientRect();
      if (!width || !height) return;
      setViewportSize(previous => previous?.width === width && previous?.height === height
        ? previous : { width, height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  });

  const fitted = useMemo(() => {
    const ratio = viewportSize ? viewportSize.width / viewportSize.height : contentWidth / contentHeight;
    const w = Math.max(contentWidth, contentHeight * ratio);
    const h = Math.max(contentHeight, contentWidth / ratio);
    return { x: (contentWidth - w) / 2, y: (contentHeight - h) / 2, w, h };
  }, [viewportSize, contentWidth, contentHeight]);


  const defaultView = useMemo(() => {
    const w = fitted.w / DEFAULT_ZOOM;
    const h = fitted.h / DEFAULT_ZOOM;
    return { x: (contentWidth - w) / 2, y: (contentHeight - h) / 2, w, h };
  }, [fitted, contentWidth, contentHeight]);

  // Start at 80% with the same centered margin used by the reset button.
  const [viewBox, setViewBox] = useState(defaultView);

  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const [spaceDown, setSpaceDown] = useState(false);

  // Reset on content size change
  useEffect(() => {
    setViewBox(defaultView);
  }, [defaultView]);

  // Space-Key zum Panen
  useEffect(() => {
    const isEditableTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const down = (e) => {
      if (e.code === 'Space' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setSpaceDown(true);
      }
    };
    const up = (e) => { if (e.code === 'Space') setSpaceDown(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    // Mittelklick oder Space+Left = immer Pan
    if (e.button === 1 || (spaceDown && e.button === 0)) {
      e.preventDefault();
      setIsPanning(true);
      panStart.current = { mx: e.clientX, my: e.clientY, vb: { ...viewBox } };
      return;
    }
    // Reingezoomt + Drag-Pan aktiviert → Left-Drag pant (bequemes Navigieren ohne Space)
    const isZoomed = viewBox.w < fitted.w - 0.5 || viewBox.h < fitted.h - 0.5;
    if (enableDragPan && isZoomed && e.button === 0) {
      e.preventDefault();
      setIsPanning(true);
      panStart.current = { mx: e.clientX, my: e.clientY, vb: { ...viewBox } };
    }
  }, [spaceDown, viewBox, contentWidth, contentHeight, enableDragPan, fitted]);

  useEffect(() => {
    if (!isPanning) return;
    const move = (e) => {
      if (!panStart.current || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const dx = (e.clientX - panStart.current.mx) * (panStart.current.vb.w / rect.width);
      const dy = (e.clientY - panStart.current.my) * (panStart.current.vb.h / rect.height);
      const vb = panStart.current.vb;
      // ViewBox-Position innerhalb des Inhalts halten
      let nx = vb.x - dx;
      let ny = vb.y - dy;
      nx = clampAxis(nx, vb.w, contentWidth);
      ny = clampAxis(ny, vb.h, contentHeight);
      setViewBox({ x: nx, y: ny, w: vb.w, h: vb.h });
    };
    const up = () => { setIsPanning(false); panStart.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [isPanning, contentWidth, contentHeight]);

  const resetView = useCallback(() => {
    setViewBox(defaultView);
  }, [defaultView]);

  const zoomBy = useCallback((factor) => {
    setViewBox((v) => {
      const minW = fitted.w / MAX_ZOOM, minH = fitted.h / MAX_ZOOM;
      const maxW = fitted.w / MIN_ZOOM, maxH = fitted.h / MIN_ZOOM;
      const newW = Math.max(minW, Math.min(maxW, v.w * factor));
      const newH = Math.max(minH, Math.min(maxH, v.h * factor));
      let nx = v.x + (v.w - newW) / 2;
      let ny = v.y + (v.h - newH) / 2;
      nx = clampAxis(nx, newW, contentWidth);
      ny = clampAxis(ny, newH, contentHeight);
      return { x: nx, y: ny, w: newW, h: newH };
    });
  }, [contentWidth, contentHeight, fitted]);

  const zoomPercent = Math.round((fitted.w / viewBox.w) * 100);

  return {
    svgRef,
    containerRef,
    viewBox,
    viewBoxString: `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`,
    handlePointerDown,
    isPanning,
    spaceDown,
    resetView,
    zoomBy,
    zoomPercent,
  };
}

export default useSvgViewport;
